import React, { useEffect, useState } from 'react';
import { Box, Divider, Paper, Popover, Typography } from '@mui/material';
import {
  DraftManual,
  DraftPolicy,
  DraftPolicySection,
  DraftPolicySubSection,
  DraftPreface,
} from 'views/App/Portal/Policy/_types/manuals/draft';
import draftManualHelper from '../../../helpers/draftManualHelper';
import useDraftPreface from 'views/App/Portal/Policy/DraftManuals/_data_sources/useDraftPreface';
import { useManualStructureContext } from '../context';
import { translate as t } from 'hooks/useTranslations';
import useDraftPolicy from 'views/App/Portal/Policy/DraftManuals/_data_sources/useDraftPolicy';
import datetimeHelper from 'helpers/datetimeHelper';
import Loading from 'views/App/Portal/Loading';

interface HoverPreviewProps {
  itemType: 'preface' | 'policy';
}
const HoverPreview = ({ itemType }: HoverPreviewProps) => {
  const { reorderedManual, hoveredItem, anchorEl, popoverPlacement, sanitizeContent } = useManualStructureContext();
  const [contentData, setContentData] = useState<DraftPolicy | DraftPreface | null>(null);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);

  const prefaceHoveredQuery = useDraftPreface(
    reorderedManual?.id,
    itemType === 'preface' ? hoveredItem?.id : undefined
  );
  const policyHoveredQuery = useDraftPolicy(reorderedManual?.id, itemType === 'policy' ? hoveredItem?.id : undefined);

  useEffect(() => {
    if (itemType === 'preface' && prefaceHoveredQuery.data) {
      setContentData(prefaceHoveredQuery?.data as DraftPreface);
      const date = prefaceHoveredQuery.data.updatedAt || prefaceHoveredQuery.data.createdAt;
      setUpdatedAt(date ? new Date(date) : null);
    } else if (itemType === 'policy' && policyHoveredQuery.data) {
      setContentData(policyHoveredQuery?.data as DraftPolicy);
      const date = policyHoveredQuery.data?.updatedAt || policyHoveredQuery.data?.createdAt;
      setUpdatedAt(date ? new Date(date) : null);
    } else {
      setContentData(null);
      setUpdatedAt(null);
    }
    return () => {
      setContentData(null);
      setUpdatedAt(null);
    };
  }, [hoveredItem, prefaceHoveredQuery.data, policyHoveredQuery.data]);

  const getSectionIndex = (section: DraftPolicySection) => {
    if (policyHoveredQuery?.data?.state !== 'DISABLED') {
      return `${draftManualHelper.getNumberManualNode(policyHoveredQuery?.data, reorderedManual as DraftManual)}.${draftManualHelper.getEnabledSectionIndex(section?.id, policyHoveredQuery?.data as DraftPolicy)}`;
    }
    return '';
  };

  const getSubSectionIndex = (section: DraftPolicySection, subSection: DraftPolicySubSection) => {
    if (policyHoveredQuery?.data?.state !== 'DISABLED')
      return `${draftManualHelper.getNumberManualNode(policyHoveredQuery?.data, reorderedManual as DraftManual)}.${draftManualHelper.getEnabledSectionIndex(section.id, policyHoveredQuery?.data as DraftPolicy)}.${draftManualHelper.getEnabledSubSectionIndex(section.id, subSection.id, policyHoveredQuery?.data as DraftPolicy)}`;
    return '';
  };

  return (
    <Popover
      id={contentData?.id}
      open={!!hoveredItem}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: popoverPlacement === 'bottom' ? 'bottom' : 'top',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: popoverPlacement === 'bottom' ? 'top' : 'bottom',
        horizontal: 'center',
      }}
      sx={{
        pointerEvents: 'none',
        mt: popoverPlacement === 'top' ? -1 : 1,

        '& .MuiPaper-root': {
          borderRadius: '12px',
        },
      }}
      disableRestoreFocus
    >
      <Paper
        sx={{
          p: 0.5,
          borderRadius: '12px',
          border: '1px solid #e4e8e8',
          backgroundColor: '#f2f6fa',
          width: '35vw',
        }}
        elevation={4}
      >
        <Paper
          sx={{
            p: 4,
            borderRadius: '12px',
            border: '1px solid #e4e8e8',
            overflow: 'hidden',
            whiteSpace: 'normal',
            textOverflow: 'ellipsis',
          }}
        >
          {prefaceHoveredQuery?.isLoading || policyHoveredQuery?.isLoading ? (
            <Box sx={{ mx: 'auto' }}>
              <Loading />
            </Box>
          ) : (
            <Box>
              <Typography
                sx={{
                  width: 'max-content',
                  px: 2,
                  py: 1,
                  fontSize: 12,
                  backgroundColor: '#c8e2f7',
                  borderRadius: 6,
                  color: '#004B87',
                  mt: 'auto',
                  mb: 2,
                }}
              >
                {itemType === 'policy'
                  ? 'Policy ' +
                    draftManualHelper.getNumberManualNode(policyHoveredQuery?.data, reorderedManual as DraftManual)
                  : 'Preface'}
              </Typography>
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: 18 }}>{contentData?.title}</Typography>
              </Box>
              <Box
                sx={{
                  maxHeight: '140px',
                  overflow: 'hidden',
                  whiteSpace: 'normal',
                  textOverflow: 'ellipsis',
                }}
              >
                {itemType === 'preface' && (
                  <Typography sx={{ mt: 3 }}>{sanitizeContent(prefaceHoveredQuery?.data?.content)}</Typography>
                )}
                {itemType === 'policy' &&
                  policyHoveredQuery?.data?.sections
                    ?.filter((section) => section.state !== 'DISABLED')
                    .map((section) => (
                      <Box
                        key={section.id}
                        sx={{ mt: 3 }}
                      >
                        <Typography
                          sx={{ fontWeight: 600, fontSize: 16 }}
                        >{`${getSectionIndex(section)} ${section.title}`}</Typography>
                        {sanitizeContent(section.content)}
                        {section.subSections
                          ?.filter((subSection) => subSection.state !== 'DISABLED')
                          .map((subSection) => (
                            <Box
                              key={subSection.id}
                              sx={{ ml: 3, mt: 2 }}
                            >
                              <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                                {`${getSubSectionIndex(section, subSection)} ${subSection.title}`}
                              </Typography>
                              {sanitizeContent(subSection.content)}
                            </Box>
                          ))}
                      </Box>
                    ))}
              </Box>
              <Divider sx={{ mt: 2, mb: 2 }} />
              {updatedAt && (
                <Typography sx={{ color: '#697586', fontWeight: 500, fontSize: 14 }}>
                  {`${t('common.updatedAt')}: ${datetimeHelper.formatDate(updatedAt.toString())}`}
                </Typography>
              )}
            </Box>
          )}
        </Paper>
      </Paper>
    </Popover>
  );
};

export default HoverPreview;
